import type { NormalizedRecord } from "./types";
import { ThemeSwitcher } from "../../lib/ThemeSwitcher";
import type { FilterState } from "./filters";
import type { RecordEventsStatus } from "./useRecordEvents";
import { formatRecordTime } from "../../lib/formatting";

interface CategoryGroup {
  category: string;
  themes: Array<{ theme: string; records: NormalizedRecord[] }>;
}

interface Props {
  groups: CategoryGroup[];
  totalCount: number;
  allRecordCount: number;
  filters: FilterState;
  categoryOptions: string[];
  themeOptions: string[];
  recordStatusOptions: string[];
  mappingErrors: string[];
  refreshing: boolean;
  eventsStatus: RecordEventsStatus;
  hasRemoteUpdate: boolean;
  onFiltersChange: (filters: FilterState) => void;
  onRefresh: () => void;
  onOpenRecord: (recordId: string, trigger: HTMLButtonElement) => void;
  onOpenCreate: () => void;
}

/**
 * Pure presentational view. Filtering and grouping are done upstream so this
 * component stays a stable rendering target for the Vitest browser tests.
 * Spec §5.6: every record card exposes a keyboard / touch / mouse reachable
 * edit entry — we use a real <button> wrapping the card surface.
 */
export function RecordsView({
  groups,
  totalCount,
  allRecordCount,
  filters,
  categoryOptions,
  themeOptions,
  recordStatusOptions,
  mappingErrors,
  refreshing,
  eventsStatus,
  hasRemoteUpdate,
  onFiltersChange,
  onRefresh,
  onOpenRecord,
  onOpenCreate,
}: Props) {
  return (
    <main className="console-page">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">记录</h1>
        <div className="flex items-center gap-2">
          <ThemeSwitcher />
          <button
            type="button"
            onClick={onOpenCreate}
            className="rounded bg-neutral-900 px-3 py-1 text-sm text-white hover:bg-neutral-700"
          >
            新建
          </button>
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="rounded border border-neutral-300 px-3 py-1 text-sm hover:bg-neutral-100"
          >
            {refreshing ? "刷新中…" : "刷新"}
          </button>
        </div>
      </header>

      {mappingErrors.length > 0 && (
        <div
          role="alert"
          className="mapping-warning mb-3 rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900"
        >
          <strong>主题类别映射存在冲突，请检查系统配置记录。</strong>
          <ul>
            {mappingErrors.map((error) => <li key={error}>{error}</li>)}
          </ul>
        </div>
      )}

      <div className="sync-status" aria-live="polite">
        <span>{eventsStatusLabel(eventsStatus)}</span>
        {hasRemoteUpdate ? (
          <button type="button" className="secondary-button" onClick={onRefresh}>
            数据有更新，点击刷新
          </button>
        ) : null}
      </div>

      <section aria-label="记录筛选" className="filter-panel">
        <label className="filter-field filter-keyword">
          <span>正文关键词</span>
          <input
            type="search"
            value={filters.keyword}
            onChange={(event) =>
              onFiltersChange({ ...filters, keyword: event.target.value })
            }
            placeholder="搜索记录正文"
          />
        </label>
        <label className="filter-field">
          <span>类别</span>
          <select
            value={filters.category}
            onChange={(event) => {
              const category = event.target.value;
              onFiltersChange({
                ...filters,
                category,
                theme: category === filters.category ? filters.theme : "",
              });
            }}
          >
            <option value="">全部类别</option>
            {categoryOptions.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </label>
        <label className="filter-field">
          <span>主题</span>
          <select
            value={filters.theme}
            onChange={(event) =>
              onFiltersChange({ ...filters, theme: event.target.value })
            }
          >
            <option value="">全部主题</option>
            {themeOptions.map((theme) => (
              <option key={theme} value={theme}>{theme}</option>
            ))}
          </select>
        </label>
        <label className="filter-field">
          <span>状态</span>
          <select
            value={filters.status}
            onChange={(event) =>
              onFiltersChange({ ...filters, status: event.target.value })
            }
          >
            <option value="">全部</option>
            {recordStatusOptions.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="secondary-button filter-reset"
          onClick={() => onFiltersChange({ keyword: "", category: "", theme: "", status: "" })}
        >
          清除筛选
        </button>
      </section>

      <p className="mb-3 text-sm text-neutral-500">
        可见 {totalCount} 条；规范状态 {recordStatusOptions.length} 项。
      </p>

      {groups.length === 0 ? (
        <div className="list-empty-state">
          <p>{allRecordCount === 0 ? "当前还没有记录。" : "没有匹配当前筛选条件的记录。"}</p>
          {allRecordCount === 0 ? (
            <button type="button" className="primary-button" onClick={onOpenCreate}>新建第一条记录</button>
          ) : null}
        </div>
      ) : (
        <ul className="space-y-6">
          {groups.map((group) => (
            <li key={group.category}>
              <h2 className="mb-2 text-base font-medium">{group.category}</h2>
              <ul className="space-y-4 pl-2">
                {group.themes.map((theme) => (
                  <li key={theme.theme}>
                    <h3 className="mb-1 text-sm font-medium text-neutral-700">
                      {theme.theme}
                    </h3>
                    <ul className="space-y-2">
                      {theme.records.map((record) => (
                        <li key={record.recordId} className="record-card">
                          <div className="record-card-meta">
                            <span>{record.status}</span>
                            <span>{record.theme || "未设主题"}</span>
                            <span>{record.category || "未设类别"}</span>
                            <span>优先级 {record.priority ?? "—"}</span>
                            <time dateTime={record.updatedAt} title={record.updatedAt}>
                              {formatRecordTime(record.updatedAt)}
                            </time>
                          </div>
                          {record.content.length > 240 ? (
                            <details className="record-content">
                              <summary>{record.content.slice(0, 240)}…</summary>
                              <p>{record.content}</p>
                            </details>
                          ) : (
                            <p className="record-content">{record.content}</p>
                          )}
                          <div className="record-card-actions">
                            <button
                              type="button"
                              onClick={(event) => onOpenRecord(record.recordId, event.currentTarget)}
                              className="secondary-button"
                              aria-label={`编辑 ${record.theme || "未设主题"} 的记录`}
                            >
                              编辑
                            </button>
                            {record.detailUrl ? (
                              <a href={record.detailUrl} target="_blank" rel="noreferrer">详情链接</a>
                            ) : null}
                            {record.isConfigRecord ? <span className="config-badge">系统配置</span> : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

function eventsStatusLabel(status: RecordEventsStatus): string {
  if (status === "connected") return "实时更新已连接";
  if (status === "reconnecting") return "实时更新正在重连";
  if (status === "unavailable") return "实时更新不可用";
  return "实时更新连接中";
}
